/*------------------------------------------------------------------------------------------------------------*/
/* Filename: product.component.ts                                                                                     */
/* Description: This file is used to manage all lottery products                                              */
/* Created: 18/Dec/2019                                                                                        */
/* Copyright (c) 2023 Lottosocial by Hatchster                                                                */
/*------------------------------------------------------------------------------------------------------------*/

import { AsyncPipe, NgClass } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
} from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { Firestore } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthSyndicateService } from "@core/AuthServices/authSyndicate/auth-syndicate.service";
import { LoadingService } from "@core/loading.service";
import { UtilityService } from "@core/utility.service";
import { ITimer } from "@interfaces/timer";
import { IonicModule, ModalController, NavController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subject, Subscription, combineLatest, concat, interval, of, throwError } from "rxjs";
import { map, take, tap, takeUntil, timeout, catchError, delay } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { JoinedSyndicateComponent } from "src/app/components/joined-syndicate/joined-syndicate.component";
import { AddNewCardComponent } from "src/app/components/payments/add-new-card/add-new-card.component";
import { RegProfileDialogComponentComponent } from "src/app/components/reg-profile-dialog-component/reg-profile-dialog-component.component";
import { StoreTopUpComponent } from "src/app/components/store-top-up/store-top-up.component";
import { GameBalanceService } from "src/app/services/game-balance.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { PaymentService } from "src/app/services/payment.service";
import { TrackedEventsService } from "src/app/services/tracked-events.service";
import { UserManagementService } from "src/app/services/user-management.service";
import { VoucherService } from "src/app/services/vouchers.service";
import type { SwiperOptions } from "swiper/types";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import { v4 as uuidv4 } from "uuid";
import { CountDownTimerComponent } from "../../../components/count-down-timer/count-down-timer.component";
import { InProgressAnimationComponent } from "../../../components/in-progress-animation/in-progress-animation.component";
import { OnboardingPopupComponent } from "../../../components/onboarding-popup/onboarding-popup.component";
import { DayPipe } from "../../../pipe/day";
import { DisplayCurrencyPipe } from "../../../pipe/display-currency.pipe";
import { WalletService } from "../../account/account-wallet/wallet.services";
import { HomeRepositoryService } from "../../home/home-repository.service";
import { SyndicateRepositoryService } from "../../syndicate/syndicate-repository.service";
import { SyndicateTicketsComponent } from "../../syndicate/syndicate-tickets/syndicate-tickets.component";
import { CheckoutComponent } from "../checkout/checkout.component";
import { ProductAnimationComponent } from "../product-animation/product-animation.component";
import { ProductInfoComponent } from "../product-info/product-info.component";
import { StorePaymentComponent } from "../store-payment/store-payment.component";
import { StoreRepositoryService } from "../store-repository.service";
import { TriggersComponent } from "../triggers/triggers.component";
import { NarratorService } from "src/app/services/narrator.service";
import { NotificationManagerService } from "src/app/services/notification-manager.service";
import { ProductPaymentFacadeService } from "../services/product-payment-facade.service";
import { ProductDrawDayUtility } from "../services/product-draw-day.utility";

@Component({
  selector: "app-product",
  templateUrl: "./product.component.html",
  styleUrls: ["./product.component.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [
    IonicModule,
    CountDownTimerComponent,
    NgClass,
    ProductAnimationComponent,
    SwiperModule,
    MatProgressSpinner,
    MatIcon,
    InProgressAnimationComponent,
    AsyncPipe,
    DisplayCurrencyPipe,
    DayPipe,
    TranslatePipe,
  ],
})
export class ProductComponent implements OnInit, OnDestroy, AfterViewInit {
  lang = "en";
  @ViewChild("numberSlider", { static: false }) numberSlider: SwiperComponent;
  selectedDiscountType = signal<string>("untilStopped");
  public payNow = signal(false);
  public runSpinner = signal(false);
  private unsubscribe$ = new Subject<void>();
  drawDay$ = new BehaviorSubject([]);
  offerRange$ = new BehaviorSubject(4);
  offerDiscount$ = new BehaviorSubject(null);
  useCredit$ = new BehaviorSubject(false);
  manageTrigger$ = new BehaviorSubject(false);
  productOffers$ = new BehaviorSubject(null);
  productToolTip$ = new BehaviorSubject(null);
  selectedProductData$ = new BehaviorSubject(null);
  queryParams = signal<Data>({});
  triggerPath;
  productName;
  storeOffers;
  creditToggle: any = false;
  creditValueTogg: any;
  line_group = [];
  offers;
  checkNearestDraw = true;
  paramDrawDay: any;
  viewAnimatin: true;
  irishProduct: boolean;
  drawCloseTime: number;
  initialPrice: number;
  journeyId: number;
  promoName: string;
  public credit: string;
  public numberOfLines: number;
  public creditRefreshStatus: Data;
  private selectedProductDataSubscription: Subscription;
  private destroy$ = new Subject<void>();
  /**
   * @description
   * This function takes the payment details and filters them to return only the payment cards that are stored in the wallet. It also sets the tokenExists variable to true if the length of the filtered array is greater than 0.
   * @param paymentDetails
   */
  paymentDetails$ = this.walletService.paymentDetails$?.pipe(takeUntil(this.destroy$),
    map((paymentDetails: any) => {
      const storedCards = this.walletService?.getFilteredWalletCards(paymentDetails);
      if (storedCards && storedCards?.length > 0) {
        this.tokenExists = true;
      }
    })
  );
  isOnlyOneWeek: boolean;
  discountPercentage: any;
  storeOffersSubscription$: Subscription;
  lotteryName: string;
  defaultLine: number = 10;
  shiftCount: number = 5;
  public showFlashSale = false;
  public flashSaleTitle = "FLASH SALE!";
  public flashSaleVoucherData: Data;
  flashSaleWidth$ = this.authService?.selectedlanguageObservable$.pipe(takeUntil(this.destroy$),
    map((res) => {
      let width = "90px";
      switch (res) {
        case "en":
          width = "82px";
          break;
        case "it":
          width = "95px";
          break;
        case "es":
          width = "88px";
          break;
        case "fr":
          width = "86px";
          break;
        default:
          width = "88px";
      }
      return width;
    })
  );

  // This function is used to get selected offer data
  initSelectedProductData(product: Data) {
    if (!product) {
      this.navCtrl.back();
      return;
    }

    if (product?.draw_day) {
      let draw_days = [];
      let selectedIndex;
      product?.draw_day.forEach((element: string, index: number) => {
        if (index === 0) {
          this.drawDay$.next([UtilityService.shortDrawDay(element)]);
        }
        if (this.paramDrawDay == UtilityService.shortDrawDay(element)) {
          draw_days.push({
            draw_day: UtilityService.shortDrawDay(element),
            isSelected: true,
          });
          selectedIndex = index;
        } else {
          draw_days.push({
            draw_day: UtilityService.shortDrawDay(element),
            isSelected: false,
          });
        }
      });
      if (!this.paramDrawDay) {
        draw_days = this.productDrawDayUtility.checkNearestDay(this, draw_days);
      }
      if (this.paramDrawDay) this.selectDrawday(draw_days[selectedIndex], draw_days, selectedIndex);
      product["draw_days"] = draw_days;
    }
    this.productOffers$.next(product);
    sessionStorage?.setItem("recentLotteryDetails", JSON?.stringify({ jackpot_value: product?.jackpot_value}));
    this.selectedProductData$.next(product);

    this.syndicateService.getUserJacktpotTrigger()?.subscribe({
      next: (data) => {
        data.forEach((val: any) => {
          if (val && Object.keys(val).length > 0) {
            if (
              val.current_jackpot?.path?.substring(data[0].current_jackpot?.path?.indexOf("/") + 1) === product?.name
            ) {
              this.manageTrigger$.next(true);
              this.triggerPath = data[0].trigger?.path;
            }
          }
        });
      },
      error: (err) => {
        return err;
      },
    });
  }
  // This function is used to get the remaining time to close the draw
  getRemainingTime(event) {
    this.drawCloseTime = event;
  }
  /**
   * This function is used to get current user balance.
   * @param {number} credit - user's credit
   */
  accountBalance$ = this.authService.userCurrentBalance$?.pipe(takeUntil(this.destroy$),
    map((res) => {
      if (!res) {
        return { credit: 5 };
      }
      return res;
    })
  );
  spinnerAnimation: boolean;
  progressBar: number = 5;
  proceed = true;
  timer$: any;
  sub: any;
  $timer: ITimer;
  showToolTip$ = new BehaviorSubject(true);
  productToolTip = this.homeRepositoryService?.toolTipText$;
  tokenExists: boolean;
  @HostListener("window:resize")
  // Ensures that the slider is updated when the window is resized
  onResize() {
    if (!this.numberSlider) return;
    this.numberSlider?.swiperRef?.update();
  }

  // This function returns a boolean value depending on the user's onboarding state and the current toolTip.
  show$ = combineLatest(this.authService.userDetails$, this.showToolTip$).pipe(takeUntil(this.destroy$),
    map(([user, toolTip]: any) => {
      if (user && user.onboard_state === "awareness" && toolTip) {
        return true;
      }
      return false;
    })
  );

  // This method is used to get the selected product data such as draw day, offer, discount, credit and use credit
  selectedProductOffers$ = combineLatest([
    this.drawDay$,
    this.offerRange$,
    this.offerDiscount$,
    this.useCredit$,
    this.selectedProductData$,
  ]).pipe(takeUntil(this.destroy$),
    map(([drawDay, selectedRange, discount, useCredit, selectedProductData]) => {
      if (!discount) {
        return;
      }
      if (useCredit) {
        if (!selectedProductData || !selectedProductData.credit) return of([]);
        this.offers = selectedProductData?.credit?.find((x: any) => {
          let draw_day = [];
          if (!x || !x.draw_day) return of([]);
          x.draw_day.forEach((val) => draw_day.push(val?.trim()));

          //show even numbers for irish product
          let range = selectedRange + 1;
          if (this.irishProduct && x?.lines == 1 && x?.lines == range) {
            selectedRange = 1;
          }

          return (
            draw_day.sort().toString() === drawDay?.sort().toString().trim() &&
            x.lines === selectedRange + 1 &&
            x.payment_type === "credit"
          );
        });
        return this.offers;
      } else {
        if (!selectedProductData || !selectedProductData?.[`${discount?.type}`]) return of([]);
        this.offers = selectedProductData?.[`${discount?.type}`]?.find((x: any) => {
          let draw_day = [];
          if (!x || !x.draw_day) return of([]);
          x.draw_day.forEach((val) => draw_day.push(val?.trim()));

          //show even numbers for irish product
          let range = selectedRange + 1;
          if (this.irishProduct && x?.lines == 5 && x?.lines == range) {
            selectedRange = 5;
          }
          return (
            draw_day.sort().toString() === drawDay.sort().toString().trim() &&
            x.lines === selectedRange + 1 &&
            x.payment_type === "cash"
          );
        });
        this.initialPrice = this.offers?.int_price?.[this.preferredCurrencySignal()];
        return this.offers;
      }
    }),
    tap((offer) => {
      if (offer?.payment_type === "credit") {
        this.accountBalance$?.subscribe({
          next: (bal: any) => {
            if (Number(offer?.price) <= bal?.credit) {
              this.creditInsufficient = false;
            } else {
              this.creditInsufficient = true;
            }
          },
          error: (err) => {
            return err;
          },
        });
      }
    })
  );

  // This code is a part of a larger function that handles the discount percentage.
  // The discount percentage is set in the storeOffers object, and it is used to
  // calculate the new price of the product. In this case the product is a subscription
  // to a magazine. The discount percentage is set to 50%.
  // This code is used to check if the user has already signed up for the magazine.
  // If the user has already signed up, the discount percentage is set to 50%.
  // If the user has not signed up for the magazine, the discount percentage is set to 0%.
creditDiscountChanged(event: string | Data) {
  try {
    if (!this.storeOffers?.cash_offer_discount?.[0]) return;

    // Normalize value (handle string OR event object)
    const value =
      typeof event === "string"
        ? event
        : event?.detail?.value ?? event?.checked;
    this.selectedDiscountType.set(value);
    if (value === "onlyOnce") {
      this.offerDiscount$.next(this.storeOffers?.cash_offer_discount?.[0]);
      this.isOnlyOneWeek = true;
    } else if (value === "untilStopped") {
      this.offerDiscount$.next(this.storeOffers?.cash_offer_discount?.[4]);
      this.discountPercentage = this.storeOffers?.cash_offer_discount[4]?.discount;
      this.isOnlyOneWeek = false;
    }

  } catch (e) {
    this.authService.errorHandlerLogs("creditDiscountChanged error:", e);
  }
}
  currentSliderIndex = 0;
  showToolTipText = false;
  config: SwiperOptions = {
    speed: 600,
    grabCursor: true,
    centeredSlides: true,
    slidesPerView: 6,
    slideToClickedSlide: true,
  };
  selectedProduct: any;
  loader = true;
  creditInsufficient = false;
  translatedToastMsg: any;
  preferredCurrency: string = CommonVariable.preferredCurrency;
  preferredCurrencySignal = signal<string>(CommonVariable.preferredCurrency);
  currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  currencySymbolSignal = signal<string>(CommonVariable.defaultCurrencySymbol);
  languageCode: string = "en";
  sqlAPICallResponse: any;
  speed: number;
  syndicateObjForTicket: Data; //used to store the syndicate object with ticket-component
  isNavigationAllowed = true;
  userId: string;
  public narratorData: Data;
  constructor(
    private firestore: Firestore,
    private navCtrl: NavController,
    private bottomSheet: MatBottomSheet,
    private dialog: MatDialog,
    public activatedRoute: ActivatedRoute,
    private storeService: StoreRepositoryService,
    private paymentService: PaymentService,
    private analytics: AngularFireAnalytics,
    private authService: AuthService,
    private bottomsheet: MatBottomSheet,
    private router: Router,
    private modalController: ModalController,
    private syndicateService: SyndicateRepositoryService,
    private homeRepositoryService: HomeRepositoryService,
    private walletService: WalletService,
    public translate: TranslateService,
    private trackedEventsService: TrackedEventsService,
    private matDailog: MatDialog,
    private voucherService: VoucherService,
    private loadingService: LoadingService,
    private GTMSagmentService: GTMSagmentService,
    private mixPanelService: MixpanelService,
    private elementRef: ElementRef,
    private AuthSyndicateService: AuthSyndicateService,
    private userManagementService: UserManagementService,
    private gameBalanceService: GameBalanceService,
    private narratorService: NarratorService,
    private notificationManagerService: NotificationManagerService,
    private productPaymentFacade: ProductPaymentFacadeService,
    private productDrawDayUtility: ProductDrawDayUtility,
  ) {
    this.speed = 500;
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = this.currencySymbolSignal();
    this.promoName = localStorage.getItem("engagement_promo") || "";
    this.authService.selectedlanguage$?.subscribe({
      next: (l: string) => {
        this.lang = l || "en";
        translate.use(l || "en");
      },
      error: (err) => {
        return err;
      },
    });

    // This method is used to get credit refresh
    this.gameBalanceService.isCreditRefresh$?.subscribe((data: Data) => {
      this.creditRefreshStatus = data?.status;
    });

    // This method is used to get constant messages for toast messages
    /* this.authService.getConstantsMsg$.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    }) */

    // This method is used to get user details such as currency, language
    this.authService.userDetails$?.subscribe({
      next: (user) => {
        this.languageCode = user?.language_code || "en";
        translate.use(this.languageCode || "en");
        this.preferredCurrencySignal.set(user?.preferred_currency || localStorage.getItem("preferred_currency_code") || CommonVariable.preferredCurrency);
        this.preferredCurrency = this.preferredCurrencySignal();

        this.authService.getCurrencyList$?.subscribe({
          next: (data: Data) => {
            const CurrencyDetails = data?.details?.find(
              (x) => x?.currency_code === this.preferredCurrencySignal()
            );
            this.currencySymbolSignal.set(CurrencyDetails?.currency_symbol || CommonVariable.defaultCurrencySymbol);
            this.currencySymbol = this.currencySymbolSignal();
          },
          error: (err) => {
            return err;
          },
        });
      },
      error: (err) => {
        return err;
      },
    });

    this.loader = true;
    // Get the current product name from the URL
    this.activatedRoute.paramMap?.subscribe({
      next: (params) => {
        if (params) {
          this.productName = params.get("product");
          this.storeService.selectedProduct$.next(this.productName);
          if (this.productName && this.productName === "Irish Lotto") {
            // Show even numbers for Irish product
            this.irishProduct = true;
          } else {
            this.irishProduct = false;
          }
        }
      },
      error: (err) => {
        return err;
      },
    });
    // This function used to get user offer Cappaing
    this.storeService.getUserOfferCapping();
    const isDiscount = localStorage.getItem("isDiscount") === "true";
    this.storeService?.iSdiscountAvailable.set(isDiscount);
  }

  // This code allows the user to slide the slider to the next slide.
  // This code is being used in the home page of the app.
  ionViewDidEnter() {
    try {
      this.storeService.lotteries$.pipe(takeUntil(this.destroy$)).subscribe((lottery: Data) => {
        // Get the lottery name from the selected product data
        this.storeService.selectedProductData$?.subscribe((product: Data) => {
          this.lotteryName = product?.name;
        });
        const selectedLottery: Data = lottery?.find((lotteryDetails) => lotteryDetails?.id === this.lotteryName);
        let swiperLines = [];
        if (this.irishProduct) {
          swiperLines = this.storeOffers?.lines?.filter(
            (line) => +line % 2 == 0 && (this.creditValueTogg ? +line : +line > 4)
          );
        } else if (!this.irishProduct) {
          swiperLines = this.storeOffers?.lines?.filter((line) => (this.creditValueTogg ? +line : +line > 4));
        }
        const defaultStoreLine =
          selectedLottery?.default_store_line && selectedLottery?.default_store_line > 0
            ? selectedLottery?.default_store_line
            : this.defaultLine;
        // Check if swiperLines is not undefined or null before using indexOf
        if (swiperLines && swiperLines.length > 0) {
          const defaultLineIndex = swiperLines?.indexOf(defaultStoreLine?.toString());
          if (defaultLineIndex && defaultLineIndex != -1) {
            this.shiftCount = defaultLineIndex;
          }
        }
        if (this.shiftCount && this.shiftCount != -1 && !this.numberOfLines) {
          this.numberSlider?.swiperRef?.slideTo(this.shiftCount, this.speed);
        }
      });
    } catch (error) {
      return error;
    }
    // Show narrator tip on product page if applicable
    this.narratorShow();
  }

  async ngOnInit() {
    const resolvedData = this.activatedRoute.snapshot.data["data"];
    this.currentSliderIndex = resolvedData?.defaults?.lines;

    await this.authService
      ?.getUid("ProductComponent")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("product_screen", {
          userId: this.userId,
        });
      })
      .catch((error: any) => {
        return error;
      });
    this.journeyId = parseInt(localStorage.getItem("journeyId") || "0", 10);
    const productData = this.homeRepositoryService
      .productToolTip()
      .then((result) => {
        this.productToolTip$.next(result);
      })
      .catch((err) => {
        return err;
      });
    // setOboardingForProduct function was commented out because it was not required now will enable it when required
    // this.setOnboardingForProduct();
    this.offerRange$.next(10);
    this.isOnlyOneWeek = false;
    this.activatedRoute.queryParams?.subscribe({
      next: (params) => {
        this.paymentService.salesParams = params;
        this.queryParams.set(params);   
        this.paramDrawDay = UtilityService.shortDrawDay(params["drawDay"]);
        if (params["numberOfLines"]) {
          this.numberOfLines = params["numberOfLines"];    
          this.offerRange$.next(Number(this.numberOfLines));
        }
        if ((params["useCredit"] || params["Usecredit"]) == "yes") {
          this.creditToggle = true;
          this.toggleCredit(this.creditToggle);
          this.useCredit$.next(true);
          this.isOnlyOneWeek = false;
          return;
        } else {
          this.isOnlyOneWeek = true;
        }
        this.creditToggle = false;
      },
      error: (err) => {
        return err;
      },
    });

    this.getInitialPrice();
    this.checkStoreFlashSaleStatus();
    this.initStoreOfferData();
    this.initSelectedProductData(resolvedData);
  }

  initStoreOfferData() {
    this.storeOffersSubscription$ = this.storeService.storeOffers$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (data && data != null && data?.cash_offer_discount?.[0]) {
          this.isOnlyOneWeek = false;
          this.storeOffers = data;
          this.offerDiscount$.next(data?.cash_offer_discount?.[4] ?? data?.cash_offer_discount?.[0]);
         // Update discount percentage only if credit is not being used based on query params
           const params = this.queryParams();
           const useCreditParam = params?.useCredit || params?.Usecredit;
           if(this.useCredit$.value && useCreditParam ) return;
              this.discountPercentage = data?.cash_offer_discount[4]?.discount;   
        }
      },
      error: (err) => {
        return err;
      },
    });
  }

  // Display narrator tip for product routes like /store/products/:product
  public narratorShow(): void {
    this.narratorService
      ?.getNarratorTipsHome()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: Data[]) => {
          this.narratorData = [] as Data;
          if (Array.isArray(response) && response.length > 0) {
            const current = this.narratorService?.getCurrentPageURL();
            const pageName = current?.includes("?") ? current.split("?")[0] : current;
            for (const tip of response) {
              if (tip?.setting?.page_name === pageName && tip?.setting?.seen === false) {
                localStorage.setItem("narratorID", tip?.id);
                this.narratorData = tip;
                break;
              }
            }
          }

          if (
            this.narratorData?.setting?.seen === false &&
            (() => {
              const current = this.narratorService?.getCurrentPageURL();
              const pageName = current?.includes("?") ? current.split("?")[0] : current;
              return this.narratorData?.setting?.page_name === pageName;
            })()
          ) {
            this.notificationManagerService.addNotification({ ...this.narratorData, type: "narrator_tip" });
          }
        },
        () => { }
      );
  }

  // This function is used to set the onboarding for the product
  private setOnboardingForProduct() {
    this.authService
      .getOnboardingStatus()
      .pipe(take(1), takeUntil(this.destroy$))
      ?.subscribe(([SyndicateOnboarding, user]) => {
        const data = SyndicateOnboarding as any;
        if (
          !data?.store &&
          window.location.href.search("products") > -1 &&
          user?.onboard_state &&
          user?.onboard_state === "onboarding"
        ) {
          const modal = this.matDailog.open(OnboardingPopupComponent, {
            data: "store",
            height: "100%",
            width: "100%",
            panelClass: "Animation",
          });
        }
      });
  }
  ionViewWillEnter() {
    // 🔥 Always reset default state
  if (this.queryParams().useCredit == "yes" || this.queryParams()?.Usecredit == "yes") return
    this.useCredit$.next(false);
    this.selectedDiscountType.set("untilStopped");
   
    this.isOnlyOneWeek = false;
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.selectedProductDataSubscription) {
      this.selectedProductDataSubscription.unsubscribe();
    }
    if (this.storeOffersSubscription$) {
      this.storeOffersSubscription$.unsubscribe();
    }
  }
  getInitialPrice() {
    this.selectedProductDataSubscription = this.selectedProductData$?.subscribe();
  }

  /**
   * @description: this function checks whether the user has enough credits and if so it will remove the credits from the users balance and will set the useCredit$ to true.
   * @param $event: this is a boolean that is passed in from the credit toggle button.
   * @return: none.
   */
  toggleCredit($event) {
    if ($event.checked == true) {
      this.showFlashSale = false;
      this.GTMSagmentService.HotJarEvent("credit_switch_on");
      if (this.promoName === "credit_syndicate_join" || this.promoName === "promo_credit_offer") {
        this.callPromoCF("seen");
      }
    }
    if ($event.checked == false) {
      this.isOnlyOneWeek = false;
      this.checkStoreFlashSaleStatus();
      if (this.promoName === "cash_syndicate_join") {
        this.callPromoCF("seen");
      }
    } 
    
    if (this.numberOfLines && this.creditToggle) {
      this.offerRange$.next(this.offerRange$?.value - 1)
    } else {
      this.useCredit$.value 
      ? this.offerRange$.next(this.offerRange$?.value + 4)
      : this.offerRange$.next(this.offerRange$?.value - 4);
    }
    this.useCredit$.next(!this.useCredit$.value);
    this.creditValueTogg = $event.checked != undefined ? $event.checked : $event;
    this.isOnlyOneWeek = true;
    this.creditDiscountChanged($event.checked || ($event == true) ? "onlyOnce" : "untilStopped");
    this.slideToMidpoint(this.creditValueTogg);
  }

  // This code will be used to check if the slider has been changed
  isSlideChanged() {
    let index = this.numberSlider?.swiperRef?.activeIndex;
    //show even numbers for irish product
    if (this.irishProduct) index = index * 2;

    if (this.irishProduct) this.creditValueTogg ? this.offerRange$.next(index + 1) : this.offerRange$.next(index + 5);
    //show even numbers for irish product
    else this.creditValueTogg ? this.offerRange$.next(index) : this.offerRange$.next(index + 4);

    this.currentSliderIndex = index;
  }

  //  Go back to the previous page
  goBack() {
    this.router.navigate(['mainTabs/tabs/store'], {
      replaceUrl: true
    });
    // this.navCtrl.back();
  }

  // Show product information
  showProductInfo() {
    if (!this.storeOffers || !this.storeOffers.descriptions || !this.storeOffers?._descriptions) return;
    this.bottomSheet.open(ProductInfoComponent, {
      panelClass: "about-product-dialog",
      backdropClass: "about-product-dialog-backdrop",
      data: {
        productDescription: this.storeOffers?._descriptions
          ? this.storeOffers?._descriptions[this.productName][this.languageCode]
          : this.storeOffers?.descriptions[this.productName],
        Back: this.storeOffers?._descriptions[this.productName].back
          ? this.storeOffers?._descriptions[this.productName].back[this.languageCode]
          : "Back",
      },
    });
  }

  // This function is used to join the syndicate
  onJoinSyndicate(): void {
    this.productPaymentFacade.joinSyndicate(this);
  }

  /**
   * Returns whether the user has enough credit to redeem the prize
   *
   * @param creditBalance The user's current credit balance
   * @param prize The cost of the prize
   * @returns true if the user does not have enough credit to redeem the prize, false otherwise
   */
  isCreditAvailable(creditBalance: number, prize: string) {
    return creditBalance < +prize;
  }

  // This function initiates the animation of the spinner
  // The animation is in 3 parts, which are defined by the 3 intervals
  // Each interval has a different speed, the first one being the fastest
  // The animation is initiated with the spinnerAnimation boolean set to true
  // The spinnerAnimation boolean is used in the html to decide the visibility of the spinner
  // The progressBar variable is used to control the width of the spinner
  // The timer$ variable is used to control the intervals
  // The sub variable is used to unsubscribe from the intervals, if the user clicks the button again
  onStartSpinner(): void {
    this.productPaymentFacade.startSpinner(this);
  }

  // This function is used to process credit payment
  onCreditPayment(): void {
    this.productPaymentFacade.makeCreditPayment(this);
  }

  // This function is used to check if the variable a is equal to the variable draw. If the variable a is equal to the variable draw, the function returns true, otherwise it returns false.
  getChecked(a, draw) {
    return a === draw ? true : false;
  }

  // Add new card bottom sheet
  openPaymentsPage() {
    const payment = this.bottomSheet.open(AddNewCardComponent, {
      panelClass: "payment-screen-bottom-sheet",
    });
  }

  // Function to manage the jackpot trigger
  manageJackpotTrigger() {
    let triggerData = {
      triggerPath: this.triggerPath,
      productName: this.productName,
    };
    const payment = this.bottomSheet.open(TriggersComponent, {
      panelClass: "jackpot-triggers-bottom-sheet",
      backdropClass: "jackpot-triggers-backdrop",
      data: triggerData,
    });
  }

  /**
   * This function will open the bottomsheet component for topup
   * @return void
   */
  callTopup() {
    if (this.creditRefreshStatus) {
      this.paymentService.getCreditRefreshCharge()?.subscribe({
        next: (data) => {
          if (data?.status === 'success') {
            setTimeout(() => {
              this.productPaymentFacade.makeCreditPayment(this);
            }, 7000)
          } else {
            this.spinnerAnimation = false;
            this.proceed = true;
            this.router.navigateByUrl(`mainTabs/tabs/store`);
            this.authService
              .getConstantsMsg("1055")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((err) => {
                return err;
              });
          }
        },
        error: (err) => {
          this.spinnerAnimation = false;
          this.proceed = true;
          return err;
        }
      });
    } else {
      this.GTMSagmentService.trackCreditTopUp();
      const storeTopUp = this.bottomsheet.open(StoreTopUpComponent, {
        panelClass: "store-top-up-bottomsheet",
      });
      storeTopUp.afterDismissed()?.subscribe(
        (response) => {
          if (response) {
            response.award_id = response?.award_id[this.preferredCurrencySignal()];
            this.routeToStorePayment(response);
          }
        },
        (err) => {
          return err;
        }
      );

      setTimeout(() => {
        this.spinnerAnimation = false;
        this.proceed = false;
        setTimeout(() => {
          this.proceed = true;
        }, 2000);
      }, 300);
    }
  }

  // This function creates a modal to display the store payment component
  async routeToStorePayment(topUpData) {
    const storePayment = await this.modalController.create({
      component: StorePaymentComponent,
      cssClass: "modal-fullScreen",
      componentProps: {
        data: this.formPaymentDataForTopUp(topUpData),
      },
    });

    return await storePayment.present();
  }

  // This function is used to create the object with the data of the top up offer selected.
  // The object is used to send the data to the payment page as a parameter.
  private formPaymentDataForTopUp(offer: any) {
    const prize = offer?.int_price?.[this.userManagementService?.selectedCurrency]
      ? offer?.int_price?.[this.userManagementService?.selectedCurrency]
      : offer?.price;
    return {
      //prize: +offer?.price,
      prize: +prize,
      checkout: true,
      draw: "",
      game: this.currencySymbol + offer.amount_credit + " top up",
      line: "",
      offer_id: offer.award_id,
      real_name: this.currencySymbol + offer.amount_credit + " top up",
      isTopUp: true,
      page: "topUp",
    };
  }

  // This function is used to convert the days to weeks
  daysToWeek(days) {
    if (Number(days) >= 0) {
      let week = Number(days) / 7;
      return week;
    }
  }

  // This function takes in two parameters: days and hours
  // It will multiply days by 24 and add the result to the hours parameter
  // It will return the sum of the two parameters
  // If the days parameter is 1, it will return the sum of the two parameters
  // If the days parameter is not 1, it will return the hours parameter
  daysToHours(days, hours) {
    if (Number(days) === 1) {
      let hour = Number(days) * 24 + Number(hours);
      return hour;
    } else {
      return hours;
    }
  }

  // This function is used to convert hours to minutes
  hourToMinutes(hours, minutes) {
    if (Number(hours) === 1) {
      let min = Number(hours) * 60 + Number(minutes);
      return min;
    } else {
      return minutes;
    }
  }

  // Selects or deselects a draw day in the draw day dropdown
  selectDrawday(day, draw_days, index) {
    this.productDrawDayUtility.selectDrawday(this, day, draw_days, index);
  }

  // This function is called when the user touches the screen.
  // It sets the value of showToolTip$ to false, which causes the tooltip to disappear.
  touch() {
    this.showToolTip$.next(false);
  }

  ngAfterViewInit() {
    this.initializeEventData();
    queueMicrotask(() => {
      this.reinitSlider();
    });
  }

  initializeEventData() {
    setTimeout(() => {
      const formattedDwellTime = this.calculateDwellTime();
      const scrollDepthValue = this.calculateSectionScrollDepth()?.toFixed(2);

      const body = {
        Lottery_Product: this.productName,
        Dwell_Time: formattedDwellTime,
        Scroll_Depth: `${scrollDepthValue}%`,
        Journey_ID: this.journeyId,
      };

      this.mixPanelService.GTMPopupInitializationForInboxNew("Lottery Page Viewed", body);
    });
  }
  calculateDwellTime() {
    const startTime = new Date();
    const endTime = new Date();
    const dwellTime = endTime.getTime() - startTime.getTime();
    return this.AuthSyndicateService.convertMillisecondsToTime(dwellTime);
  }

  calculateSectionScrollDepth() {
    const sectionElement = this.elementRef?.nativeElement?.querySelector("#productScroll");
    if (sectionElement) {
      const sectionHeight = sectionElement?.offsetHeight;
      const sectionTop = sectionElement?.getBoundingClientRect()?.top;
      const windowHeight = window?.innerHeight;

      // Calculate scroll depth relative to the section
      if (sectionTop >= -sectionHeight && sectionTop <= windowHeight && sectionHeight > 0) {
        const visibleHeight = Math.max(0, Math.min(sectionHeight, windowHeight + sectionTop) - Math.max(0, sectionTop));
        return (visibleHeight / sectionHeight) * 100;
      } else {
        return 0;
      }
    }
  }

  slideToMidpoint(data: boolean) {
    if (!this.numberSlider || !this.numberSlider.swiperRef) {
      this.authService.errorHandlerLogs('numberSlider or swiperRef is not defined');
      return;
    }

    let slideIndex;
    if (this.irishProduct && data) {
      slideIndex = 4;
    } else {
      slideIndex = data ? 9 : 5;
    }

    this.numberSlider.swiperRef.slideTo(slideIndex, 0);
  }

  // To check and show flash sale UI on product page
  private checkStoreFlashSaleStatus(): void {
    this.authService?.userDetails$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (userData: Data) => {
        if (!userData) {
          return;
        }
        const flashSalesStoreBannerStatus = userData?.flash_sales_store_banner || false;
        if (flashSalesStoreBannerStatus) {
          this.checkVoucherValidationForFlashSale();
        }
      },
      error: (err) => {
        return err;
      }
    })
  }

  private checkVoucherValidationForFlashSale() {
    try {
      this.voucherService
        .getVoucherList()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (res: Data[]) => {
            const currentTime = new Date().getTime();
            const filteredVouchers = res.filter((voucher) => {
              const voucherExpiryDate = voucher?.expiry_date?.toDate()?.getTime();
              return (
                voucher?.discount_voucher === true &&
                voucher?.isRedeemed === false &&
                voucher?.hide === false &&
                voucher?.flash_sale === true &&
                voucherExpiryDate > currentTime &&
                (voucher?.supplier?.toLowerCase() === "general" ||
                  voucher?.supplier?.toLowerCase() === this.productName?.toLowerCase())
              );
            }); // Filter for discount vouchers
            if (filteredVouchers?.length > 0) {
              this.flashSaleVoucherData = filteredVouchers[0];
              this.showFlashSale = true;
            }
          },
          error: (err) => {
            return err;
          },
        });
    } catch (error) {
      return error;
    }
  }

  callPromoCF(action: string) {
    if (this.promoName === "cash_syndicate_join" || this.promoName === "promo_credit_offer") {
      this.authService.displayPromo(action, this.promoName);
    }
  }
  // To reinitialize the slider
  reinitSlider() {
    if (!this.numberSlider?.swiperRef) {
      requestAnimationFrame(() => this.reinitSlider());
      return;
    }
    if (this.shiftCount && this.shiftCount != -1 && !this.numberOfLines) {
      this.numberSlider?.swiperRef?.slideTo(this.shiftCount, this.speed);
    }
    if (Number(this.numberOfLines)) {
      const slideToParams = Number(this.numberOfLines) - 1;
      this.numberSlider?.swiperRef?.slideTo(slideToParams, this.speed);
    }
  }

  getProductName(data: Data): string {
    const name = data?.real_name ?? data?.content?.[this.lang]?.real_name;

    if (!name) return "";

    const translated = this.translate.instant(`dynamic_value.${name}`);
    return translated.includes("dynamic_value") ? name : translated;
  }

  shouldRenderNumber(number: number): boolean {
    if (this.irishProduct && number % 2 !== 0) {
      return false;
    }

    return this.creditValueTogg ? true : number > 4;
  }

  getNumberClasses(number: number) {
    const adjustedNumber = this.creditValueTogg ? number : number - 4;

    return {
      'slider-number-active': this.currentSliderIndex + 1 === adjustedNumber,
      'active-slider-1':
        this.currentSliderIndex === adjustedNumber ||
        this.currentSliderIndex + 2 === adjustedNumber,
    };
  }
}
